
/** @jsxImportSource react */
import React, { useMemo, useState, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import { useAuth } from '../contexts/auth'
import { useTranslation } from '../i18n'
import { api } from '../services/api'
import {
  Package,
  Search,
  ChevronDown,
  ChevronUp,
  ChevronsUpDown,
  Calendar,
  ArrowRight,
  X,
  CheckCircle,
  Clock,
  FileText,
  DollarSign,
  Ship,
  Plus,
  SlidersHorizontal,
  CalendarRange,
  MoreHorizontal,
  Inbox,
  RefreshCcw,
  ExternalLink,
  ChevronLeft,
  ChevronRight,
  ListChecks,
} from 'lucide-react'
import { Order, OrderStatus, UserRole, PaymentStatus, OrderType, User } from '../types'
import { Modal } from '../components/ui/Modal'
import NewOrderPage from './NewOrderPage'

// Utility function for status colors
const getStatusColor = (status: OrderStatus) => {
    switch (status) {
        case OrderStatus.Received: return 'bg-secondary-100 text-secondary-700';
        case OrderStatus.PriceChecking: return 'bg-secondary-200 text-secondary-800';
        case OrderStatus.Quoted: return 'bg-secondary-300 text-secondary-800';
        case OrderStatus.Confirmed: return 'bg-primary-100 text-primary-700';
        case OrderStatus.Purchased: return 'bg-primary-200 text-primary-800';
        case OrderStatus.Shipped: return 'bg-primary-300 text-primary-800';
        case OrderStatus.Delivered: return 'bg-primary-500 text-white';
        case OrderStatus.Cancelled: return 'bg-secondary-800 text-secondary-100';
        default: return 'bg-secondary-100 text-secondary-700';
    }
}

const getStatusIcon = (status: OrderStatus) => {
    switch (status) {
        case OrderStatus.Received: return <Clock className="h-4 w-4" />;
        case OrderStatus.PriceChecking: return <ListChecks className="h-4 w-4" />;
        case OrderStatus.Quoted: return <FileText className="h-4 w-4" />;
        case OrderStatus.Confirmed: return <CheckCircle className="h-4 w-4" />;
        case OrderStatus.Purchased: return <DollarSign className="h-4 w-4" />;
        case OrderStatus.Shipped: return <Ship className="h-4 w-4" />;
        case OrderStatus.Delivered: return <Package className="h-4 w-4" />;
        case OrderStatus.Cancelled: return <X className="h-4 w-4" />;
        default: return <Clock className="h-4 w-4" />;
    }
}

// Tab Button component
const TabButton: React.FC<{ label: string; isActive: boolean; onClick: () => void; icon: React.ReactNode }> = ({ label, isActive, onClick, icon }) => (
    <button
        onClick={onClick}
        className={`px-4 py-3 font-medium text-sm transition-colors rounded-t-lg focus:outline-none flex items-center gap-2 ${isActive ? 'border-b-2 border-primary-500 text-primary-600 bg-primary-50/50' : 'border-b-2 border-transparent text-secondary-500 hover:text-primary-500 hover:bg-secondary-50'}`}
    >
        {icon}
        {label}
    </button>
)

// Types for sorting
type SortKey = keyof Order | 'clientName' | 'assignedStaffName'
type SortDirection = 'asc' | 'desc'

// Sort icon component
const SortIcon: React.FC<{ direction: SortDirection | null }> = ({ direction }) => {
    if (!direction) return <ChevronsUpDown className="w-4 h-4 text-secondary-400" />
    if (direction === 'asc') return <ChevronUp className="w-4 w-4" />
    return <ChevronDown className="w-4 w-4" />
}

// Sortable header component
const SortableHeader: React.FC<{
    label: string;
    sortKey: SortKey;
    currentSortKey: SortKey;
    currentSortDirection: SortDirection;
    onSort: (key: SortKey) => void;
}> = ({
    label,
    sortKey,
    currentSortKey,
    currentSortDirection,
    onSort,
}) => {
        const isSorting = currentSortKey === sortKey
        return (
            <th className="p-3 font-medium text-secondary-600">
                <button
                    onClick={() => onSort(sortKey)}
                    className={`flex items-center gap-1 hover:text-primary-600 ${isSorting ? 'text-primary-600' : ''}`}
                >
                    {label}
                    <SortIcon direction={isSorting ? currentSortDirection : null} />
                </button>
            </th>
        )
    }

// Skeleton loader for table
const TableSkeleton: React.FC<{ isClient: boolean }> = ({ isClient }) => (
    <div className="animate-pulse">
        {/* Desktop Skeleton */}
        <table className="w-full hidden md:table">
            <thead>
                <tr>
                    <th className="p-3"><div className="h-5 w-24 bg-secondary-200 rounded"></div></th>
                    {!isClient && (<th className="p-3"><div className="h-5 w-32 bg-secondary-200 rounded"></div></th>)}
                    <th className="p-3"><div className="h-5 w-48 bg-secondary-200 rounded"></div></th>
                    {!isClient && (<th className="p-3"><div className="h-5 w-32 bg-secondary-200 rounded"></div></th>)}
                    <th className="p-3"><div className="h-5 w-24 bg-secondary-200 rounded"></div></th>
                    <th className="p-3"><div className="h-5 w-20 bg-secondary-200 rounded"></div></th>
                    <th className="p-3"><div className="h-5 w-16 bg-secondary-200 rounded"></div></th>
                    <th className="p-3"><div className="h-5 w-12 bg-secondary-200 rounded"></div></th>
                </tr>
            </thead>
            <tbody>
                {[...Array(8)].map((_, i) => (
                    <tr key={i} className="border-b">
                        <td className="p-3"><div className="h-5 w-full bg-secondary-200 rounded"></div></td>
                        {!isClient && (<td className="p-3"><div className="h-5 w-full bg-secondary-200 rounded"></div></td>)}
                        <td className="p-3"><div className="h-5 w-full bg-secondary-200 rounded"></div></td>
                        {!isClient && (<td className="p-3"><div className="h-5 w-full bg-secondary-200 rounded"></div></td>)}
                        <td className="p-3"><div className="h-5 w-full bg-secondary-200 rounded"></div></td>
                        <td className="p-3"><div className="h-5 w-full bg-secondary-200 rounded"></div></td>
                        <td className="p-3"><div className="h-5 w-full bg-secondary-200 rounded"></div></td>
                        <td className="p-3"><div className="h-5 w-full bg-secondary-200 rounded"></div></td>
                    </tr>
                ))}
            </tbody>
        </table>
        {/* Mobile Skeleton */}
        <div className="md:hidden space-y-4 p-5">
            {[...Array(5)].map((_, i) => (
                <div key={i} className="p-4 border rounded-xl bg-white shadow-sm">
                    <div className="flex justify-between items-start mb-2">
                        <div>
                            <div className="h-5 w-24 mb-2 bg-secondary-200 rounded"></div>
                            <div className="h-6 w-40 bg-secondary-200 rounded"></div>
                        </div>
                        <div className="h-6 w-20 rounded-full bg-secondary-200"></div>
                    </div>
                    <div className="flex justify-between items-center mb-4">
                        <div className="h-4 w-28 bg-secondary-200 rounded"></div>
                        <div className="h-5 w-16 rounded-full bg-secondary-200"></div>
                    </div>
                    <div className="flex justify-between items-center border-t pt-3 mt-3">
                        <div className="h-7 w-20 bg-secondary-200 rounded"></div>
                        <div className="h-5 w-24 bg-secondary-200 rounded"></div>
                    </div>
                </div>
            ))}
        </div>
    </div>
)

// Order Card Component
const OrderCard: React.FC<{ order: Order; navigate: ReturnType<typeof useNavigate>; t: (key: string) => string; formatCurrency: (num: number | null | undefined) => string; }> = ({ order, navigate, t, formatCurrency }) => {
    const statusClass = getStatusColor(order.status)
    const statusIcon = getStatusIcon(order.status)
    return (
        <div
            className="p-5 bg-white rounded-xl border border-secondary-200 shadow-sm hover:shadow-md transition-all duration-300 cursor-pointer transform hover:translate-y-[-4px] animate-fade-in"
            onClick={() => navigate(`/orders/${order.id}`)}
        >
            <div className="flex justify-between items-start mb-4">
                <div>
                    <span className="text-xs text-secondary-500 font-medium">{t('orderId')}</span>
                    <h4 className="font-semibold text-primary-600">{order.orderId}</h4>
                </div>
                <div className={`px-3 py-1.5 rounded-full text-xs font-medium flex items-center gap-1.5 ${statusClass}`}>
                    {statusIcon}
                    <span>{t(order.status.toLowerCase())}</span>
                </div>
            </div>
            <h3 className="font-medium text-secondary-900 mb-3 line-clamp-1 text-lg">{order.productName}</h3>
            <div className="flex justify-between items-center text-sm text-secondary-500 mb-4">
                <div className="flex items-center gap-2">
                    <Calendar className="w-3.5 h-3.5" />
                    <span>{new Date(order.createdAt).toLocaleDateString()}</span>
                </div>
                <span className="px-2 py-1 bg-secondary-100 rounded-full text-xs">{t(order.orderType.toLowerCase())}</span>
            </div>
            <div className="flex justify-between items-center border-t border-secondary-100 pt-4">
                <div className="font-semibold text-secondary-900">{formatCurrency(order.finalPrice)}</div>
                <button className="text-primary-600 hover:text-primary-700 flex items-center text-sm font-medium group">
                    {t('viewDetails')}
                    <ArrowRight className={`w-4 h-4 ms-1 group-hover:ms-2 rtl:rotate-180 transition-all duration-300`} />
                </button>
            </div>
        </div>
    )
}

// Main Orders Page Component
const OrdersPage = () => {
    const { user } = useAuth()
    const navigate = useNavigate()
    const { t, dir, formatCurrency } = useTranslation();

    // State for filters and sorting
    const [searchTerm, setSearchTerm] = useState('')
    const [statusFilter, setStatusFilter] = useState<OrderStatus | 'all'>('all')
    const [paymentStatusFilter, setPaymentStatusFilter] = useState<PaymentStatus | 'all'>('all')
    const [startDate, setStartDate] = useState('')
    const [endDate, setEndDate] = useState('')
    const [activeTab, setActiveTab] = useState<OrderType | 'all'>('all')
    const [sortKey, setSortKey] = useState<SortKey>('createdAt')
    const [sortDirection, setSortDirection] = useState<SortDirection>('desc')
    const [isFiltersVisible, setIsFiltersVisible] = useState(false)
    const [isNewOrderModalOpen, setIsNewOrderModalOpen] = useState(false);

    // Real-time data fetching
    const [orders, setOrders] = useState<Order[]>([]);
    const [users, setUsers] = useState<User[]>([]);
    const [isLoading, setIsLoading] = useState(true);

    useEffect(() => {
        if (!user?.id) return;

        // Only set loading to true if we don't have orders yet to prevent shimmer on every user update
        if (orders.length === 0) setIsLoading(true);
        
        // Use the synchronous listenToOrders which accepts the user object directly
        const unsubOrders = api.listenToOrders(user, (newOrders) => {
            setOrders(newOrders);
            setIsLoading(false);
        });

        let unsubUsers: (() => void) | undefined;
        if (user.role === UserRole.Admin || user.role === UserRole.Staff) {
            unsubUsers = api.listenToUsers((newUsers) => {
                setUsers(newUsers);
            });
        }
        
        return () => {
            if (unsubOrders) unsubOrders();
            if (unsubUsers) unsubUsers();
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [user.id, user.role]);

    // Create a map of user IDs to names
    const userMap = useMemo(() => {
        if (!users.length) return {}
        return users.reduce(
            (acc, u) => {
                acc[u.id] = u.name
                return acc
            },
            {} as Record<string, string>,
        )
    }, [users])

    // Handle sort column click
    const handleSort = (key: SortKey) => {
        if (sortKey === key) {
            setSortDirection((prev) => (prev === 'asc' ? 'desc' : 'asc'))
        } else {
            setSortKey(key)
            setSortDirection('asc')
        }
    }

    // Filter and sort orders
    const sortedAndFilteredOrders = useMemo(() => {
        const baseOrders = orders;
        
        const augmentedOrders =
            user?.role === UserRole.Admin || user?.role === UserRole.Staff
                ? baseOrders.map((order) => ({
                    ...order,
                    clientName: userMap[order.clientId] || '',
                    assignedStaffName: order.assignedStaffId
                        ? userMap[order.assignedStaffId] || ''
                        : '',
                }))
                : baseOrders
        const filtered = augmentedOrders.filter((order) => {
            const clientName = (order as any).clientName || ''
            const assignedStaffName = (order as any).assignedStaffName || ''
            const matchesTab = activeTab === 'all' || order.orderType === activeTab
            const matchesSearch =
                searchTerm === '' ||
                order.orderId.toLowerCase().includes(searchTerm.toLowerCase()) ||
                order.productName.toLowerCase().includes(searchTerm.toLowerCase()) ||
                (user?.role !== UserRole.Client &&
                    (clientName.toLowerCase().includes(searchTerm.toLowerCase()) ||
                        assignedStaffName.toLowerCase().includes(searchTerm.toLowerCase())))
            const matchesStatus =
                statusFilter === 'all' || order.status === statusFilter
            const matchesPaymentStatus =
                paymentStatusFilter === 'all' ||
                order.paymentStatus === paymentStatusFilter
            const orderDate = order.createdAt.substring(0, 10)
            const matchesStartDate = !startDate || orderDate >= startDate
            const matchesEndDate = !endDate || orderDate <= endDate
            return (
                matchesTab &&
                matchesSearch &&
                matchesStatus &&
                matchesPaymentStatus &&
                matchesStartDate &&
                matchesEndDate
            )
        })
        return filtered.sort((a, b) => {
            const aVal = a[sortKey as keyof typeof a]
            const bVal = b[sortKey as keyof typeof b]
            if (aVal === bVal) return 0
            if (aVal === null || aVal === undefined) return 1
            if (bVal === null || bVal === undefined) return -1
            let comparison = 0
            if (typeof aVal === 'number' && typeof bVal === 'number') {
                comparison = aVal > bVal ? 1 : -1
            } else {
                comparison = String(aVal).localeCompare(String(bVal))
            }
            return sortDirection === 'asc' ? comparison : -comparison
        })
    }, [
        orders,
        user,
        userMap,
        searchTerm,
        statusFilter,
        paymentStatusFilter,
        startDate,
        endDate,
        activeTab,
        sortKey,
        sortDirection,
    ])

    return (
        <div className="relative min-h-screen w-full bg-white overflow-hidden">
            <div className="absolute -top-24 -left-24 w-96 h-96 bg-primary-500/5 rounded-full mix-blend-multiply filter blur-3xl opacity-70"></div>
            <div className="absolute -bottom-24 -right-24 w-96 h-96 bg-secondary-500/5 rounded-full mix-blend-multiply filter blur-3xl opacity-70"></div>

            <div className="container mx-auto relative z-10">
                {/* Header with animated entrance */}
                <div className="mb-8 animate-fade-in">
                    <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4 mb-8">
                        <div>
                            <h1 className="text-3xl font-bold text-secondary-900 mb-2">{t('orders')}</h1>
                            <p className="text-secondary-600">{t('orders_page_description')}</p>
                        </div>
                        <div className="flex flex-wrap gap-3">
                            {user?.role !== UserRole.Staff && (
                                <button
                                    onClick={() => setIsNewOrderModalOpen(true)}
                                    className="inline-flex items-center px-5 py-2.5 bg-gradient-to-r from-primary-600 to-primary-500 text-white rounded-xl text-sm font-medium hover:from-primary-700 hover:to-primary-600 transition-all shadow-sm transform hover:translate-y-[-1px] hover:shadow-md"
                                >
                                    <Plus className="w-4 h-4 me-2" />
                                    {t('newOrder')}
                                </button>
                            )}
                        </div>
                    </div>
                </div>

                {/* Main content card with enhanced styling */}
                <div className="bg-white rounded-xl border border-secondary-200 shadow-md overflow-hidden mb-8 animate-fade-in">
                    {/* Enhanced tab navigation */}
                    <div className="flex items-center border-b border-secondary-200 overflow-x-auto bg-secondary-50/50">
                        <TabButton label={t('all')} icon={<Package size={16} />} isActive={activeTab === 'all'} onClick={() => setActiveTab('all')} />
                        <TabButton label={t('sourcing')} icon={<Search size={16} />} isActive={activeTab === OrderType.Sourcing} onClick={() => setActiveTab(OrderType.Sourcing)} />
                        <TabButton label={t('shipping')} icon={<Ship size={16} />} isActive={activeTab === OrderType.Shipping} onClick={() => setActiveTab(OrderType.Shipping)} />
                        <TabButton label={t('general')} icon={<FileText size={16} />} isActive={activeTab === OrderType.General} onClick={() => setActiveTab(OrderType.General)} />
                    </div>
                    {/* Enhanced search and filters */}
                    <div className="p-5 border-b border-secondary-200 bg-white">
                        <div className="flex flex-col sm:flex-row gap-4">
                            <div className="relative flex-grow">
                                <div className="absolute inset-y-0 start-0 flex items-center ps-3 pointer-events-none">
                                    <Search className="h-4 w-4 text-secondary-400" />
                                </div>
                                <input
                                    type="text"
                                    placeholder={t('searchPlaceholder')}
                                    value={searchTerm}
                                    onChange={(e) => setSearchTerm(e.target.value)}
                                    className="w-full ps-10 pe-4 py-2.5 rounded-xl border border-secondary-200 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent transition-all duration-200"
                                />
                            </div>
                            <button
                                onClick={() => setIsFiltersVisible(!isFiltersVisible)}
                                className="inline-flex items-center px-4 py-2.5 bg-white border border-secondary-200 rounded-xl text-sm font-medium text-secondary-600 hover:bg-secondary-50 transition-all shadow-sm"
                            >
                                <SlidersHorizontal className="w-4 h-4 me-2" />
                                {t('filters')}
                                {isFiltersVisible ? <ChevronUp className="w-4 h-4 ms-2" /> : <ChevronDown className="w-4 h-4 ms-2" />}
                            </button>
                        </div>
                        {/* Enhanced filters panel */}
                        {isFiltersVisible && (
                            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5 mt-5 pt-5 border-t border-secondary-200 animate-fade-in">
                                <div>
                                    <label className="block text-sm font-medium text-secondary-700 mb-2">{t('status')}</label>
                                    <select
                                        value={statusFilter}
                                        onChange={(e) => setStatusFilter(e.target.value as OrderStatus | 'all')}
                                        className="w-full px-3 py-2.5 bg-white border border-secondary-200 rounded-xl shadow-sm focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                                    >
                                        <option value="all">{t('allStatuses')}</option>
                                        {Object.values(OrderStatus).filter(status => user?.role !== UserRole.Client || status !== OrderStatus.PriceChecking).map((status) => (
                                            <option key={status} value={status}>{t(status.toLowerCase())}</option>
                                        ))}
                                    </select>
                                </div>
                                <div>
                                    <label className="block text-sm font-medium text-secondary-700 mb-2">{t('paymentStatus')}</label>
                                    <select
                                        value={paymentStatusFilter}
                                        onChange={(e) => setPaymentStatusFilter(e.target.value as PaymentStatus | 'all')}
                                        className="w-full px-3 py-2.5 bg-white border border-secondary-200 rounded-xl shadow-sm focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                                    >
                                        <option value="all">{t('allPaymentStatuses')}</option>
                                        {Object.values(PaymentStatus).map((status) => (<option key={status} value={status}>{t(status.toLowerCase())}</option>))}
                                    </select>
                                </div>
                                <div>
                                    <label className="block text-sm font-medium text-secondary-700 mb-2">{t('startDate')}</label>
                                    <div className="relative">
                                        <div className="absolute inset-y-0 start-0 flex items-center ps-3 pointer-events-none"><CalendarRange className="h-4 w-4 text-secondary-400" /></div>
                                        <input type="date" value={startDate} onChange={(e) => setStartDate(e.target.value)} className="w-full ps-10 pe-4 py-2.5 rounded-xl border border-secondary-200 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent" />
                                    </div>
                                </div>
                                <div>
                                    <label className="block text-sm font-medium text-secondary-700 mb-2">{t('endDate')}</label>
                                    <div className="relative">
                                        <div className="absolute inset-y-0 start-0 flex items-center ps-3 pointer-events-none"><CalendarRange className="h-4 w-4 text-secondary-400" /></div>
                                        <input type="date" value={endDate} onChange={(e) => setEndDate(e.target.value)} className="w-full ps-10 pe-4 py-2.5 rounded-xl border border-secondary-200 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent" />
                                    </div>
                                </div>
                            </div>
                        )}
                    </div>
                    {/* Table and card list */}
                    <div className="overflow-x-auto">
                        {isLoading ? (
                            <div className="p-5">
                                <TableSkeleton isClient={user?.role === UserRole.Client} />
                            </div>
                        ) : (
                            <>
                                {/* Enhanced Desktop Table */}
                                <table className="w-full text-start hidden md:table">
                                    <thead className="bg-secondary-50">
                                        <tr>
                                            <SortableHeader label={t('orderId')} sortKey="orderId" currentSortKey={sortKey} currentSortDirection={sortDirection} onSort={handleSort} />
                                            {user?.role !== UserRole.Client && (<SortableHeader label={t('client')} sortKey="clientName" currentSortKey={sortKey} currentSortDirection={sortDirection} onSort={handleSort} />)}
                                            <SortableHeader label={t('product')} sortKey="productName" currentSortKey={sortKey} currentSortDirection={sortDirection} onSort={handleSort} />
                                            {user?.role !== UserRole.Client && (<SortableHeader label={t('assignedTo')} sortKey="assignedStaffName" currentSortKey={sortKey} currentSortDirection={sortDirection} onSort={handleSort} />)}
                                            <SortableHeader label={t('orderType')} sortKey="orderType" currentSortKey={sortKey} currentSortDirection={sortDirection} onSort={handleSort} />
                                            <SortableHeader label={t('date')} sortKey="createdAt" currentSortKey={sortKey} currentSortDirection={sortDirection} onSort={handleSort} />
                                            <SortableHeader label={t('status')} sortKey="status" currentSortKey={sortKey} currentSortDirection={sortDirection} onSort={handleSort} />
                                            <SortableHeader label={t('total')} sortKey="finalPrice" currentSortKey={sortKey} currentSortDirection={sortDirection} onSort={handleSort} />
                                            <th className="p-3 font-medium text-secondary-600">{t('actions')}</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {sortedAndFilteredOrders.map((order) => (
                                            <tr key={order.id} className="border-b hover:bg-secondary-50 transition-colors cursor-pointer" onClick={() => navigate(`/orders/${order.id}`)}>
                                                <td className="p-3 font-medium text-primary-600">{order.orderId}</td>
                                                {user?.role !== UserRole.Client && (<td className="p-3 font-medium">{(order as any).clientName}</td>)}
                                                <td className="p-3">{order.productName}</td>
                                                {user?.role !== UserRole.Client && (<td className="p-3 text-secondary-600">{(order as any).assignedStaffName || t('unassigned')}</td>)}
                                                <td className="p-3">
                                                    <span className={`px-2.5 py-1 text-xs font-medium rounded-full ${order.orderType === OrderType.Sourcing ? 'bg-primary-100 text-primary-800' : order.orderType === OrderType.Shipping ? 'bg-secondary-200 text-secondary-800' : 'bg-secondary-300 text-secondary-800'}`}>
                                                        {t(order.orderType.toLowerCase())}
                                                    </span>
                                                </td>
                                                <td className="p-3 text-secondary-600">{new Date(order.createdAt).toLocaleDateString()}</td>
                                                <td className="p-3">
                                                    <span className={`px-2.5 py-1 text-xs font-medium rounded-full flex items-center gap-1.5 w-fit ${getStatusColor(order.status)}`}>
                                                        {getStatusIcon(order.status)}
                                                        <span>{t(order.status.toLowerCase())}</span>
                                                    </span>
                                                </td>
                                                <td className="p-3 font-medium">{formatCurrency(order.finalPrice)}</td>
                                                <td className="p-3">
                                                    <div className="flex items-center">
                                                        <button onClick={(e) => { e.stopPropagation(); navigate(`/orders/${order.id}`) }} className="text-primary-600 hover:text-primary-700 font-medium transition-colors flex items-center">
                                                            <ExternalLink size={16} className="me-1" />{t('view')}
                                                        </button>
                                                        <button onClick={(e) => e.stopPropagation()} className="ms-4 text-secondary-400 hover:text-secondary-600"><MoreHorizontal size={16} /></button>
                                                    </div>
                                                </td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                                {/* Enhanced Mobile Card List */}
                                <div className="md:hidden p-5 grid grid-cols-1 sm:grid-cols-2 gap-5">
                                    {sortedAndFilteredOrders.map((order) => (<OrderCard key={order.id} order={order} navigate={navigate} t={t} dir={dir} formatCurrency={formatCurrency} />))}
                                </div>
                                {/* Enhanced empty state */}
                                {!isLoading && sortedAndFilteredOrders.length === 0 && (
                                    <div className="text-center py-16 px-4 animate-fade-in">
                                        <div className="w-20 h-20 bg-secondary-100 rounded-full mx-auto flex items-center justify-center mb-6"><Inbox className="w-10 h-10 text-secondary-400" /></div>
                                        <h3 className="text-xl font-bold text-secondary-900 mb-2">{t('noOrdersFound')}</h3>
                                        <p className="text-secondary-500 mb-8 max-w-md mx-auto">{t('tryChangingFilters')}</p>
                                        <div className="flex flex-wrap justify-center gap-4">
                                            <button onClick={() => { setStatusFilter('all'); setPaymentStatusFilter('all'); setStartDate(''); setEndDate(''); setSearchTerm(''); }} className="inline-flex items-center px-5 py-2.5 bg-white border border-secondary-200 rounded-xl text-sm font-medium text-secondary-600 hover:bg-secondary-50 transition-all shadow-sm">
                                                <RefreshCcw className="w-4 h-4 me-2" />
                                                {t('resetFilters')}
                                            </button>
                                            <button onClick={() => setIsNewOrderModalOpen(true)} className="inline-flex items-center px-5 py-2.5 bg-gradient-to-r from-primary-600 to-primary-500 text-white rounded-xl text-sm font-medium hover:from-primary-700 hover:to-primary-600 transition-all shadow-sm">
                                                <Plus className="w-4 h-4 me-2" />
                                                {t('createOrder')}
                                            </button>
                                        </div>
                                    </div>
                                )}
                            </>
                        )}
                    </div>
                    {/* Enhanced pagination */}
                    {sortedAndFilteredOrders.length > 0 && (
                        <div className="flex items-center justify-between p-5 border-t border-secondary-200 bg-secondary-50/50">
                            <div className="text-sm text-secondary-600">
                                {t('showing')}{' '}<span className="font-medium">{sortedAndFilteredOrders.length}</span>{' '}{t('of')}{' '}<span className="font-medium">{sortedAndFilteredOrders.length}</span>{' '}{t('results')}
                            </div>
                            <div className="flex gap-2">
                                <button disabled className="px-4 py-2 border border-secondary-200 rounded-lg text-sm text-secondary-400 bg-secondary-50 cursor-not-allowed flex items-center">
                                    {dir === 'rtl' ? <ChevronRight className="w-4 h-4 me-1" /> : <ChevronLeft className="w-4 h-4 me-1" />}
                                    {t('previous')}
                                </button>
                                <button disabled className="px-4 py-2 border border-secondary-200 rounded-lg text-sm text-secondary-400 bg-secondary-50 cursor-not-allowed flex items-center">
                                    {t('next')}
                                    {dir === 'rtl' ? <ChevronLeft className="w-4 h-4 ms-1" /> : <ChevronRight className="w-4 h-4 ms-1" />}
                                </button>
                            </div>
                        </div>
                    )}
                </div>
            </div>
            {isNewOrderModalOpen && <Modal size="3xl" title={t('createNewOrder')} onClose={() => setIsNewOrderModalOpen(false)}><NewOrderPage onClose={() => setIsNewOrderModalOpen(false)} /></Modal>}
        </div>
    )
}
export default OrdersPage